import { useLocation } from "react-router-dom";

import { ROUTE_CONFIG } from "@/routes/config";

import { useLoginAs } from "@/pages/Private/pages/SupportDashboard/hooks/useLoginAs";
import { useGetCompanyQuery } from "@/pages/Private/redux/companies/companies.api";
import { useGetAdminFeaturesQuery } from "@/pages/Private/redux/feature/admin-feature.api";
import {
	useGetCompanyTicketCountsQuery,
	useGetStaffAssigneesQuery,
	useGetStaffTicketQuery,
	useGetStaffTicketsQuery,
} from "@/pages/Private/redux/tickets/tickets-admin.api";

import { isSupportUiActive } from "../../../utils/supportUiActive";
import { openTicketCount, OTHER_TICKETS_LIMIT, otherCompanyTickets } from "../utils/customerCard";
import { pickSupportUser } from "../utils/supportUser";
import { readQueueIds, ticketNeighbours } from "../utils/ticketNeighbours";
import { StaffTicketDetailState } from "./staffTicketDetailController";
import { useTicketTriage } from "./useTicketTriage";

const isNotFound = (error: unknown): boolean =>
	typeof error === "object" && error !== null && (error as { status?: unknown }).status === 404;

/**
 * Everything the staff detail reads and does (PREC-3940), in one place: the ticket, then — once it
 * is there — its company, the per-company counts, the roster, the admin feature list (for the
 * Support-UI landing rule) and the company's queue page for "Other tickets". Neighbours come from
 * the router state the queue's row click left (decision 4); "Log in as" picks the company's
 * Signalist user and lands on the customer's ticket only where its Support UI is on (decision 3).
 */
export const useStaffTicketDetail = (ticketId: number): StaffTicketDetailState => {
	const location = useLocation();
	const invalidId = Number.isNaN(ticketId);
	const { data: ticket, isLoading, error } = useGetStaffTicketQuery(ticketId, { skip: invalidId });
	const loaded = ticket !== undefined;
	const companyId = ticket?.companyId ?? 0;

	const { data: company } = useGetCompanyQuery(companyId, { skip: !loaded });
	const { data: counts } = useGetCompanyTicketCountsQuery(undefined, { skip: !loaded });
	const { data: assignees } = useGetStaffAssigneesQuery(undefined, { skip: !loaded });
	const { data: adminFeatures } = useGetAdminFeaturesQuery(undefined, { skip: !loaded });
	const { data: companyQueue } = useGetStaffTicketsQuery(
		{ page: 1, limit: OTHER_TICKETS_LIMIT + 1, companyId },
		{ skip: !loaded }
	);
	const { triage, isTriaging } = useTicketTriage(ticket);
	const { loginAs, isLoading: isLoggingIn } = useLoginAs();

	if (invalidId || isNotFound(error)) {
		return { kind: "NOT_FOUND" };
	}

	if (error) {
		return { kind: "ERROR" };
	}

	if (isLoading || !ticket) {
		return { kind: "LOADING" };
	}

	const queueIds = readQueueIds(location.state);
	const supportUser = pickSupportUser(company?.users ?? []);
	const supportUiActive = isSupportUiActive(adminFeatures, ticket.companyId);
	const landing = supportUiActive ? `${ROUTE_CONFIG.SUPPORT}/${ticket.id}` : ROUTE_CONFIG.LISTS;

	return {
		kind: "READY",
		controller: {
			ticket,
			company,
			openTicketCount: openTicketCount(counts, ticket.companyId),
			supportUiActive,
			assignees: assignees ?? [],
			otherTickets: otherCompanyTickets(companyQueue?.data ?? [], ticket.id),
			neighbours: ticketNeighbours(queueIds, ticket.id),
			queueState: queueIds.length ? { queueIds } : null,
			supportUser,
			triage,
			isTriaging,
			loginAsSupportUser: () =>
				supportUser ? loginAs(supportUser.id, landing) : Promise.resolve(false),
			isLoggingIn,
		},
	};
};
